import React, { useEffect, useRef, useState } from "react";
import { Grid, notification, Select as SelectTwo } from "antd";
import { useTranslation } from "react-i18next";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useDispatch, useSelector } from "react-redux";

import { Select, Input, TextArea, Button } from "components";
import {
  CopyIcon,
  TrashIcon,
  PlusIcon,
  DescriptiveIcon,
  RadioIcon,
  CheckboxIcon,
  MultiLineIcon,
  PullDownIcon,
  CircleNoBackgroundIcon,
  XIcon,
  RectangleIcon,
  MoreIcon,
  DeleteIcon,
  SendCompleteIcon,
  LinkedIcon,
} from "icons";
import { localStorageHelper, uuid } from "utils";
import { WrapperErrorMessage, ErrorMessage } from "theme/CommonStyle";

import {
  IQuestionnaire,
  QuestionnaireAnswer,
  QuestionnairesQuestion,
} from "models/questionnaires/questionnairesDetail";
import { httpStatus } from "configs/httpStatus";
import {
  DeleteQuestionnaireActionCreators,
  QuestionnairesActionCreators,
} from "redux/rootActions";
import { TextConfirm } from "screens/messageDelivery/MessageDelivery.style";
import { deleteQuestionnairesDataResponse } from "redux/questionnaires/deleteQuestionnaires/deleteQuestionnairesStates";

import {
  TextAreaStyled,
  WrapperIconItemQuestion,
  WrapperPlus,
  WrapperIconSelect,
  WrapperAdd,
  WrapperTitleInputTextarea,
  WrapperQuestion,
  QuestionBodyLeft,
  QuestionBodyRight,
  WrapperItem,
  QuestionDraftBody,
  QuestionDraftTitle,
  WrapperDraft,
  WrapperTooltip,
  DragSection,
  ConfirmStatusModalStyled,
  QuestionDraftTitleLeft,
  QuestionDraftTitleRight,
} from "./Questions.style";
import { CheckboxStyled } from "../userList/UserList.style";
import { ConfirmModal } from "./components";

const { useBreakpoint } = Grid;

const iconList: any = {
  radio: <CircleNoBackgroundIcon fill="#CCCCCC" />,
  checkbox: <RectangleIcon fill="#CCCCCC" />,
};

interface IAnswer {
  id: number;
  answer_text: string;
  link_questionnaire_question_id?: number;
  _destroy?: boolean;
  is_other?: boolean;
}

export interface IQuestionnaires {
  answerType: string;
  answerTitle: string;
  answerFreetext: string;
  isRequired: boolean;
  isFreeText: boolean;
  answersArr: IAnswer[];
}

interface IQuestion {
  question: QuestionnairesQuestion;
  index: number;
  setQuestionnaires: React.Dispatch<React.SetStateAction<IQuestionnaire>>;
  questionnaires: IQuestionnaire;
  error: any;
  setError: any;
  linkQuestionairesOptions: any;
  setLinkQuestionairesOptions: any;
  handleLinkQuestionOptions: any;
  defaultOptions: any;
  createQuestion: any;
}

const bannedValue = ["not-select", "add-question"];

interface IItemByType {
  type: string;
  idxQuestion: number;
  setQuestionnaires: React.Dispatch<React.SetStateAction<IQuestionnaire>>;
  linkQuestionairesOptions: { id: number; value: number; name: string }[];
  setLinkQuestionairesOptions: any;
  answer: IAnswer;
  error: any;
  setError: any;
  isAnswerLinkQuestion: boolean;
  questionnaires: any;
  defaultOptions: any;
  createQuestion: any;
}

const MovableItem = ({ name, index, moveCardHandler, children }: any) => {
  const ref = useRef<any>(null);
  const [, drop] = useDrop({
    accept: "Our first type",
    hover(item: any, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      const clientOffset: any = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveCardHandler(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: "Our first type",
    item: { index },
    end: (item, monitor) => {
      const dropResult: any = monitor.getDropResult();
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.4 : 1;
  const transform = isDragging ? "rotate(2deg)" : "rotate(0deg)";
  drag(drop(ref));

  if (name) {
    return (
      <WrapperQuestion
        ref={ref}
        className="movable-item"
        style={{
          opacity,
          transform,
          transition: "all 0.5s ease-in-out",
        }}
      >
        {children}
      </WrapperQuestion>
    );
  }

  return (
    <WrapperQuestion
      ref={ref}
      className="movable-item"
      style={{
        opacity,
        transform,
        transition: "all 0.5s ease-in-out",
      }}
    >
      {children}
    </WrapperQuestion>
  );
};

const ItemByType: React.FC<IItemByType> = ({
  type,
  idxQuestion,
  setQuestionnaires,
  linkQuestionairesOptions,
  setLinkQuestionairesOptions,
  answer,
  error,
  setError,
  isAnswerLinkQuestion,
  questionnaires,
  defaultOptions,
  createQuestion,
}) => {
  const { t }: any = useTranslation("translation", { keyPrefix: "questions" });
  const { t: t1 }: any = useTranslation("translation", {
    keyPrefix: "user-list",
  });

  const handleOnChangeAnswer = (answerID: number, idx: number) => (e: any) => {
    setQuestionnaires((prevState) => ({
      ...prevState,
      questionnaire_questions: (prevState.questionnaire_questions || []).map(
        (question, index) => {
          if (idx === index) {
            return {
              ...question,
              questionnaire_answers: question.questionnaire_answers.map(
                (a: any) => {
                  if (a.id === answerID) {
                    return {
                      ...a,
                      answer_text: e.target.value,
                    };
                  }
                  return a;
                }
              ),
            };
          }
          return question;
        }
      ),
    }));

    if (error) {
      setError((prevState: any) => ({
        ...prevState,
        questionnaire_questions: prevState.questionnaire_questions.map(
          (question: any, index: number) => {
            if (idx === index) {
              return {
                ...question,
                questionnaire_answers: question.questionnaire_answers.map(
                  (a: any) => {
                    if (a.id === error.id) {
                      return {
                        ...a,
                        is_active: true,
                        answer_text: e.target.value.length
                          ? ""
                          : t("enter-answer"),
                      };
                    }
                    return a;
                  }
                ),
              };
            }
            return question;
          }
        ),
      }));
    }
  };

  const returnAnswer = (position: any) => {
    if (
      questionnaires?.questionnaire_questions &&
      questionnaires?.questionnaire_questions.length
    ) {
      if (typeof position === "number") {
        return questionnaires.questionnaire_questions.find(
          (question: any) => question.question_position === position
        );
      } else return undefined;
    } else return undefined;
  };

  const handleOnChangeSelect = (answerID: number, idx: number) => (e: any) => {
    const clone = {
      ...questionnaires,
      questionnaire_questions: (
        questionnaires.questionnaire_questions || []
      ).map((question: any, index: any) => {
        if (idx === index) {
          return {
            ...question,
            questionnaire_answers: question.questionnaire_answers.map(
              (a: any) => {
                if (a.id === answerID) {
                  if (returnAnswer(e)) {
                    return {
                      ...a,
                      link_questionnaire_question_id:
                        e === "add-question" ? "not-select" : e,
                      link_questionnaire_question: {
                        ...returnAnswer(e),
                        question_position: e,
                      },
                    };
                  } else {
                    return {
                      ...a,
                      link_questionnaire_question_id:
                        e === "add-question" ? "not-select" : e,
                      link_questionnaire_question: {
                        question_position: e,
                      },
                    };
                  }
                }
                return a;
              }
            ),
          };
        }
        return question;
      }),
    };

    const arr: any = [];

    clone.questionnaire_questions.forEach((item: any) => {
      if (item.questionnaire_answers.length) {
        item.questionnaire_answers.forEach((ans: any) => {
          if (ans.link_questionnaire_question_id) {
            arr.push(ans.link_questionnaire_question_id);
          }
        });
      }
    });

    const cloneOptions: any = [];

    linkQuestionairesOptions.forEach((item: any) => {
      if (bannedValue.includes(item.value)) return;
      if (arr.includes(item.value)) {
        cloneOptions.push({
          ...item,
          disabled: true,
        });
      } else {
        cloneOptions.push({
          ...item,
          disabled: false,
        });
      }
    });

    let fillbg = {
      ...clone,
      questionnaire_questions: clone.questionnaire_questions.map(
        (item: any) => {
          if (arr.includes(item.question_position)) {
            return {
              ...item,
              is_link_question: true,
            };
          }

          return {
            ...item,
            is_link_question: false,
          };
        }
      ),
    };

    if (e === "add-question") {
      fillbg = {
        ...fillbg,
        questionnaire_questions: [
          ...fillbg.questionnaire_questions,
          {
            id: uuid(),
            answer_type: "",
            question_content: "",
            question_summary: "",
            is_required: false,
            is_link_question: false,
            is_free_text: false,
            question_position: questionnaires.questionnaire_questions
              ? questionnaires.questionnaire_questions.length + 1
              : 1,
            questionnaire_answers: [],
          },
        ],
      };

      setError((prevState: any) => ({
        ...prevState,
        questions: "",
        questionnaire_questions: [
          ...prevState.questionnaire_questions,
          {
            id: uuid(),
            questionnaire_answers: [],
          },
        ],
      }));
    }

    setLinkQuestionairesOptions([...defaultOptions, ...cloneOptions]);
    setQuestionnaires(fillbg);

    if (error) {
      setError((prevState: any) => ({
        ...prevState,
        questionnaire_questions: prevState.questionnaire_questions.map(
          (question: any, index: number) => {
            if (idx === index) {
              return {
                ...question,
                questionnaire_answers: question.questionnaire_answers.map(
                  (a: any) => {
                    if (a.id === error.id) {
                      return {
                        ...a,
                        is_active: true,
                        link_questionnaire_question_id: "",
                      };
                    }
                    return a;
                  }
                ),
              };
            }
            return question;
          }
        ),
      }));
    }
  };

  const checkLinkQuestion = (
    questions: QuestionnairesQuestion[],
    index: number
  ) => {
    if (!questions.length) return false;

    return questions.some((quest: QuestionnairesQuestion) =>
      quest.questionnaire_answers.some(
        (answer: QuestionnaireAnswer) =>
          answer.link_questionnaire_question?.id === questions[index].id &&
          !answer._destroy
      )
    );
  };

  const handleDelete = (answerID: number, idxQues: number, errorID: any) => {
    let removedAnswerValue: any;

    const questionairesQuestions = {
      ...questionnaires,
      questionnaire_questions: (
        questionnaires.questionnaire_questions || []
      ).map((question: any, index: number) => {
        if (idxQues === index) {
          return {
            ...question,
            questionnaire_answers: question.questionnaire_answers
              .map((a: any) => {
                if (a.id === answerID) {
                  if (a?.link_questionnaire_question_id) {
                    removedAnswerValue = Number(
                      a?.link_questionnaire_question_id
                    );
                    return {
                      ...a,
                      _destroy: true,
                      link_questionnaire_question_id: null,
                    };
                  }
                  return {
                    ...a,
                    _destroy: true,
                  };
                }
                return a;
              })
              .filter(
                (question: any) =>
                  (question.id && typeof question.id === "number") ||
                  !question._destroy
              ),
          };
        }
        return question;
      }),
    };

    const tempQuestionnaireQuestions = [
      ...(questionairesQuestions.questionnaire_questions || []),
    ];

    const questionsSet: QuestionnairesQuestion[] = [];

    if (tempQuestionnaireQuestions.length) {
      tempQuestionnaireQuestions.map(
        (quest: QuestionnairesQuestion, questIndex: number) => {
          questionsSet.push({
            ...quest,
            is_link_question: checkLinkQuestion(
              tempQuestionnaireQuestions,
              questIndex
            ),
          });
        }
      );
    }
    //enaled the option is choosed
    const newOptions: any = [];

    linkQuestionairesOptions.forEach((op: any) => {
      if (bannedValue.includes(op.value)) return;
      if (removedAnswerValue === op.value) {
        newOptions.push({
          ...op,
          disabled: false,
        });
      } else {
        newOptions.push(op);
      }
    });
    setLinkQuestionairesOptions([...defaultOptions, ...newOptions]);
    // setQuestionnaires(questionairesQuestions);
    setQuestionnaires({
      ...questionnaires,
      questionnaire_questions: questionsSet,
    });

    setError((prevState: any) => ({
      ...prevState,
      questionnaire_questions: (prevState.questionnaire_questions || []).map(
        (question: any, index: number) => {
          if (idxQues === index) {
            return {
              ...question,
              questionnaire_answers: question.questionnaire_answers.filter(
                (ans: any) => ans.id !== errorID
              ),
            };
          }
          return question;
        }
      ),
    }));
  };

  const handleRenderIcons = (type: string) => {
    if (type === "pulldown") return "";

    return iconList[type];
  };

  return (
    <WrapperItem>
      {handleRenderIcons(type)}
      <Input
        value={!answer?._destroy ? answer.answer_text : ""}
        height={31}
        fsLabel={16}
        onChange={handleOnChangeAnswer(answer.id, idxQuestion)}
        disabled={answer.is_other}
        error={error?.is_active && error?.answer_text ? error?.answer_text : ""}
      />
      <XIcon
        onClick={() => {
          handleDelete(answer.id, idxQuestion, error?.id);
        }}
        fill="#FB4E4E"
      />
      {isAnswerLinkQuestion && (
        <Select
          height="31px"
          width="230px"
          marginForm="0 0 0 16px"
          options={linkQuestionairesOptions}
          value={answer.link_questionnaire_question_id}
          // onChange={handleOnChangeSelect(answer.id, idxQuestion)}
          onSelect={handleOnChangeSelect(answer.id, idxQuestion)}
          placeholder={t1("please-select")}
          dropdownRender={(menu) => <div style={{ maxWidth: 230 }}>{menu}</div>}
        />
      )}
    </WrapperItem>
  );
};

const Question: React.FC<IQuestion> = ({
  question,
  index,
  setQuestionnaires,
  questionnaires,
  error,
  setError,
  linkQuestionairesOptions,
  setLinkQuestionairesOptions,
  handleLinkQuestionOptions,
  defaultOptions,
  createQuestion,
}) => {
  const { t }: any = useTranslation("translation", { keyPrefix: "questions" });
  const { t: t1 }: any = useTranslation("translation", {
    keyPrefix: "user-list",
  });
  const { t: t2 }: any = useTranslation("translation", {
    keyPrefix: "class-and-activity-management",
  });

  const screens = useBreakpoint();

  const optionQuestions = [
    {
      id: 0,
      value: "text",
      name: (
        <WrapperIconSelect>
          <DescriptiveIcon fill="#cccccc" />
          <span>{t("descriptive-formula")}</span>
        </WrapperIconSelect>
      ),
    },
    {
      id: 1,
      value: "multiLine",
      name: (
        <WrapperIconSelect>
          <MultiLineIcon fill="#cccccc" />
          <span>{t("multi-line-descriptive-expression")}</span>
        </WrapperIconSelect>
      ),
    },
    {
      id: 2,
      value: "radio",
      name: (
        <WrapperIconSelect>
          <RadioIcon fill="#cccccc" />
          <span>{t("radio-button")}</span>
        </WrapperIconSelect>
      ),
    },
    {
      id: 3,
      value: "checkbox",
      name: (
        <WrapperIconSelect>
          <CheckboxIcon fill="#cccccc" />
          <span>{t("checkbox")}</span>
        </WrapperIconSelect>
      ),
    },
    {
      id: 4,
      value: "pulldown",
      name: (
        <WrapperIconSelect>
          <PullDownIcon fill="#cccccc" />
          <span>{t("pull-down")}</span>
        </WrapperIconSelect>
      ),
    },
  ];

  const handleSortAnswerQuestion = (questionArr?: any) => {
    const clone = [...questionArr];
    return clone.sort(
      (a: any, b: any) => Number(a.is_other) - Number(b.is_other)
    );
  };

  const handleAddChoices = (index: number, other?: string) => {
    setQuestionnaires((prevState: any) => ({
      ...prevState,
      questionnaire_questions: prevState.questionnaire_questions.map(
        (question: any, idx: number) => {
          if (idx === index) {
            if (other) {
              return {
                ...question,
                questionnaire_answers: handleSortAnswerQuestion([
                  ...question.questionnaire_answers,
                  {
                    id: uuid(),
                    answer_text: t("question-other"),
                    is_other: true,
                  },
                ]),
              };
            }
            return {
              ...question,
              questionnaire_answers: handleSortAnswerQuestion([
                ...question.questionnaire_answers,
                {
                  id: uuid(),
                  answer_text: "",
                  is_other: false,
                },
              ]),
            };
          }
          return question;
        }
      ),
    }));

    setError((prevState: any) => ({
      ...prevState,
      questionnaire_questions: prevState.questionnaire_questions.map(
        (question: any, idx: number) => {
          if (idx === index) {
            if (other) {
              return {
                ...question,
                questionnaire_answers: handleSortAnswerQuestion([
                  ...question.questionnaire_answers,
                  {
                    id: uuid(),
                    is_active: false,
                    answer_text: "",
                    is_other: true,
                  },
                ]),
              };
            }
            return {
              ...question,
              questionnaire_answers: handleSortAnswerQuestion([
                ...question.questionnaire_answers,
                {
                  id: uuid(),
                  is_active: false,
                  answer_text: t("enter-answer"),
                  is_other: false,
                },
              ]),
            };
          }
          return question;
        }
      ),
    }));
  };

  const handleOnChange =
    (index: number, name: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const questionsArr: any[] = [];

      if (questionnaires && questionnaires.questionnaire_questions?.length) {
        questionnaires.questionnaire_questions.map(
          (question: any, idx: number) => {
            if (idx === index) {
              questionsArr.push({
                ...question,
                [name]: e.target.value,
              });
            } else {
              questionsArr.push(question);
            }
          }
        );
      }

      setQuestionnaires((prevState: any) => ({
        ...prevState,
        questionnaire_questions: prevState.questionnaire_questions.map(
          (question: any, idx: number) => {
            if (idx === index) {
              return {
                ...question,
                [name]: e.target.value,
              };
            }
            return question;
          }
        ),
      }));

      setError((prevState: any) => ({
        ...prevState,
        questionnaire_questions: prevState.questionnaire_questions.map(
          (question: any, idx: number) => {
            if (idx === index) {
              return {
                ...question,
                [name]: e.target.value.length ? "" : t("enter-answer"),
              };
            }
            return question;
          }
        ),
      }));
    };

  const checkLinkQuestion = (
    questions: QuestionnairesQuestion[],
    index: number
  ) => {
    if (!questions.length) return false;

    return questions.some((quest: QuestionnairesQuestion) =>
      quest.questionnaire_answers.some(
        (answer: QuestionnaireAnswer) =>
          answer.link_questionnaire_question?.id === questions[index].id
      )
    );
  };

  const handleOnChangeQuestionType = (index: number) => (value: string) => {
    const tempQuestionnaireQuestions = [
      ...(questionnaires.questionnaire_questions || []),
    ];
    const questionsSet: QuestionnairesQuestion[] = [];
    if (tempQuestionnaireQuestions.length) {
      tempQuestionnaireQuestions[index] = {
        ...tempQuestionnaireQuestions[index],
        answer_type: value,
        is_answer_link_question: false,
        questionnaire_answers: [],
      };

      tempQuestionnaireQuestions.map(
        (quest: QuestionnairesQuestion, questIndex: number) => {
          questionsSet.push({
            ...quest,
            is_link_question: checkLinkQuestion(
              tempQuestionnaireQuestions,
              questIndex
            ),
          });
        }
      );
    }

    setQuestionnaires({
      ...questionnaires,
      questionnaire_questions: questionsSet,
    });
    // setQuestionnaires((prevState: any) => ({
    //   ...prevState,
    //   questionnaire_questions: prevState.questionnaire_questions.map(
    //     (question: any, idx: number) => {
    //       if (idx === index) {
    //         return {
    //           ...question,
    //           answer_type: value,
    //           is_answer_link_question: false,
    //           questionnaire_answers: [],
    //           // questionnaire_answers: question.questionnaire_answers.map(
    //           //   (a: any) => {
    //           //     return {
    //           //       ...a,
    //           //       _destroy: true,
    //           //     };
    //           //   }
    //           // ),
    //         };
    //       }
    //       return question;
    //     }
    //   ),
    // }));

    setError((prevState: any) => ({
      ...prevState,
      questionnaire_questions: prevState.questionnaire_questions.map(
        (question: any, idx: number) => {
          if (idx === index) {
            return {
              ...question,
              answer_type: value ? "" : t("enter-answer"),
              questionnaire_answers: [],
            };
          }
          return question;
        }
      ),
    }));
  };

  const checkboxList: any = {
    1: "is_required",
    2: "is_answer_link_question",
    3: "is_free_text",
  };

  const handleCheckbox = (index: number, type: number) => (e: any) => {
    const text = checkboxList[type];
    const linkedQuestionArr: any = [];

    let obj = {
      ...questionnaires,
      questionnaire_questions: (
        questionnaires.questionnaire_questions || []
      ).map((question, idx) => {
        if (idx === index) {
          if (text === "is_free_text" && !e.target.checked) {
            return {
              ...question,
              question_summary: "",
              [text]: e.target.checked,
            };
          }
          if (text === "is_answer_link_question" && !e.target.checked) {
            return {
              ...question,
              [text]: e.target.checked,
              questionnaire_answers: question.questionnaire_answers.map(
                (as: any) => {
                  if (
                    (as?.link_questionnaire_question &&
                      Object.keys(as.link_questionnaire_question).length) ||
                    as?.link_questionnaire_question_id
                  ) {
                    const {
                      link_questionnaire_question,
                      link_questionnaire_question_id,
                      ...rest
                    } = as;
                    linkedQuestionArr.push(link_questionnaire_question_id);
                    return {
                      ...rest,
                    };
                  }
                  return as;
                }
              ),
            };
          }
          return {
            ...question,
            [text]: e.target.checked,
          };
        }
        return question;
      }),
    };

    obj = {
      ...obj,
      questionnaire_questions: obj.questionnaire_questions.map((qs: any) => {
        if (linkedQuestionArr.includes(qs.question_position)) {
          return {
            ...qs,
            is_link_question: false,
          };
        }
        return qs;
      }),
    };

    setQuestionnaires(obj);

    if (text === "is_answer_link_question") {
      if (!e.target.checked) {
        const newOptions: any = [];
        linkQuestionairesOptions.forEach((op: any) => {
          if (bannedValue.includes(op.value)) return;
          if (linkedQuestionArr.includes(op.value)) {
            newOptions.push({
              ...op,
              disabled: false,
            });
          } else {
            newOptions.push(op);
          }
        });

        setLinkQuestionairesOptions([...defaultOptions, ...newOptions]);
      } else {
        setLinkQuestionairesOptions([
          // ...defaultOptions,
          ...handleLinkQuestionOptions(obj?.questionnaire_questions),
        ]);
      }
    }
  };

  const handleCopyQuestion = (
    item: QuestionnairesQuestion,
    idxQuestion: number
  ) => {
    setQuestionnaires((prevState: any) => ({
      ...prevState,
      questionnaire_questions: [
        ...prevState.questionnaire_questions,
        {
          id: uuid(),
          answer_type: item.answer_type,
          question_content: item.question_content,
          question_summary: item.question_summary,
          is_required: item.is_required,
          is_free_text: item.is_free_text,
          questionnaire_answers: item.questionnaire_answers,
          question_position: questionnaires.questionnaire_questions
            ? questionnaires.questionnaire_questions.length + 1
            : 1,
          copy_idx:
            "-copy" +
            (prevState.questionnaire_questions.filter((i: any) => i.copy_idx)
              .length +
              1),
        },
      ],
    }));
    setError((prevState: any) => ({
      ...prevState,
      questionnaire_questions: [
        ...prevState.questionnaire_questions,
        {
          id: uuid(),
          questionnaire_answers:
            prevState.questionnaire_questions[idxQuestion]
              .questionnaire_answers,
        },
      ],
    }));
  };

  const updateSelected = (questionArr: QuestionnairesQuestion[]) => {
    const tempQuestionsArr = [...questionArr];

    questionArr.map((questionItem: QuestionnairesQuestion, index: number) => {
      if (questionItem.is_answer_link_question) {
        if (
          questionItem.questionnaire_answers.some(
            (quest: QuestionnaireAnswer) =>
              quest.link_questionnaire_question_id !==
              questionArr.find(
                (question: QuestionnairesQuestion) =>
                  question.id === quest.link_questionnaire_question?.id
              )?.question_position
          )
        ) {
          tempQuestionsArr[index] = {
            ...tempQuestionsArr[index],
            questionnaire_answers: questionArr[index].questionnaire_answers.map(
              (updateQuestion: QuestionnaireAnswer) => {
                const updatePosition = questionArr.find(
                  (question: QuestionnairesQuestion) =>
                    question.id ===
                    updateQuestion.link_questionnaire_question?.id
                )?.question_position;
                return {
                  ...updateQuestion,
                  link_questionnaire_question_id: updatePosition,
                  link_questionnaire_question: {
                    ...updateQuestion.link_questionnaire_question,
                    question_position: updatePosition,
                  },
                };
              }
            ),
          };
        }
      }
      if (questionItem.is_link_question) {
        if (
          questionArr.some(
            (question: QuestionnairesQuestion) =>
              question.is_answer_link_question &&
              question.questionnaire_answers.some(
                (answer: QuestionnaireAnswer) =>
                  answer.link_questionnaire_question?.id ===
                  tempQuestionsArr[index].id
              )
          )
        ) {
          tempQuestionsArr[index] = {
            ...tempQuestionsArr[index],
          };
        } else {
          tempQuestionsArr[index] = {
            ...tempQuestionsArr[index],
            is_link_question: false,
          };
        }
      }
    });

    return tempQuestionsArr;
  };

  const handleDeleteQuestion = (item: QuestionnairesQuestion) => {
    let index = -1;
    let index2 = -1;
    if (item.is_link_question) {
      index =
        questionnaires.questionnaire_questions &&
        questionnaires.questionnaire_questions.length
          ? questionnaires.questionnaire_questions.findIndex(
              (item2: QuestionnairesQuestion) => {
                if (item2.questionnaire_answers.length) {
                  if (
                    item2.questionnaire_answers.some(
                      (ans: any) =>
                        ans.link_questionnaire_question_id ===
                        item.question_position
                    )
                  ) {
                    index2 = item2.questionnaire_answers.findIndex(
                      (ans: any) =>
                        ans.link_questionnaire_question_id ===
                        item.question_position
                    );
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return false;
                }
              }
            )
          : -1;
    }

    const deleteQuestions: QuestionnairesQuestion[] = [];
    const questionsArr: QuestionnairesQuestion[] = [];
    const tempQuestionnaire =
      questionnaires.questionnaire_questions &&
      questionnaires.questionnaire_questions.length
        ? [...questionnaires.questionnaire_questions]
        : [];

    if (tempQuestionnaire.length) {
      tempQuestionnaire.map((question) => {
        const tempQuestion = { ...question };
        if (
          index >= 0 &&
          index2 >= 0 &&
          question.question_position === index + 1 &&
          question.id !== item.id
        ) {
          tempQuestion.questionnaire_answers[index2] = {
            ...tempQuestion.questionnaire_answers[index2],
            link_questionnaire_question_id: "not-select",
            link_questionnaire_question: {
              question_position: "not-select",
            },
          };

          // return tempQuestion;
          questionsArr.push(tempQuestion);
        } else if (question.id === item.id) {
          if (typeof item.id === "number") {
            if (!item.is_answer_link_question) {
              deleteQuestions.push({
                ...question,
                is_answer_link_question: false,
                _destroy: true,
                // question_position: null,
              });
            } else {
              deleteQuestions.push({
                ...question,
                is_answer_link_question: false,
                questionnaire_answers: question.questionnaire_answers.map(
                  (answer: QuestionnaireAnswer) => {
                    return {
                      ...answer,
                      link_questionnaire_question_id: null,
                      link_questionnaire_question: {
                        question_position: null,
                      },
                    };
                  }
                ),
                _destroy: true,
                // question_position: null,
              });
            }
          }
          // return {
          //   ...question,
          //   _destroy: true,
          // };
        } else {
          if (question.question_position > item.question_position) {
            questionsArr.push({
              ...tempQuestion,
              question_position: tempQuestion.question_position - 1,
            });
          } else {
            questionsArr.push(question);
          }
        }
      });
    }

    setQuestionnaires({
      ...questionnaires,
      questionnaire_questions: updateSelected(questionsArr),
      delete_questions: [
        ...(questionnaires.delete_questions || []),
        ...deleteQuestions,
      ],
    });

    setError((prevState: any) => ({
      ...prevState,
      questionnaire_questions: [...prevState.questionnaire_questions].filter(
        (err: any, index: number) => index !== item.question_position - 1
      ),
    }));
    // setQuestionnaires((prevState) => ({
    //   ...prevState,
    //   questionnaire_questions: (prevState.questionnaire_questions || []).map(
    //     (question) => {
    //       const tempQuestion = { ...question };
    //       if (
    //         index >= 0 &&
    //         index2 >= 0 &&
    //         question.question_position === index + 1
    //       ) {
    //         tempQuestion.questionnaire_answers[index2] = {
    //           ...tempQuestion.questionnaire_answers[index2],
    //           link_questionnaire_question_id: "not-select",
    //           link_questionnaire_question: {
    //             question_position: "not-select",
    //           },
    //         };

    //         return tempQuestion;
    //       }
    //       if (question.id === item.id) {
    //         return {
    //           ...question,
    //           _destroy: true,
    //         };
    //       }
    //       return question;
    //     }
    //   ),
    // }));
  };

  const handleCheckIsOtherExist = (arr: any, key: string) => {
    if (!arr.length) return false;

    const clone = handleSortAnswerQuestion(arr);
    const lastItem = clone[clone.length - 1];

    return lastItem[key];
  };

  const handleRenderItem = (answerType: string) => {
    return (
      <React.Fragment>
        {question.questionnaire_answers.length > 0 &&
          question.questionnaire_answers
            .filter((item) => !item._destroy)
            .map((answer, idx) => {
              return (
                <ItemByType
                  type={answerType}
                  key={answer.id}
                  idxQuestion={index}
                  answer={answer}
                  isAnswerLinkQuestion={question.is_answer_link_question}
                  linkQuestionairesOptions={linkQuestionairesOptions}
                  setLinkQuestionairesOptions={setLinkQuestionairesOptions}
                  setQuestionnaires={setQuestionnaires}
                  error={error?.questionnaire_answers[idx]}
                  setError={setError}
                  questionnaires={questionnaires}
                  defaultOptions={defaultOptions}
                  createQuestion={createQuestion}
                />
              );
            })}

        {question.answer_type && !error?.questionnaire_answers.length && (
          <div
            style={{
              color: "#FB4E4E",
              fontSize: 16,
            }}
          >
            {t("please-add-choice")}
          </div>
        )}

        {answerType && (
          <WrapperAdd>
            <div
              onClick={() => {
                handleAddChoices(index);
              }}
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <PlusIcon
                fill="#2AC769"
                style={{
                  width: "16px",
                  height: "22px",
                  marginRight: "5px",
                }}
              />
              <span className="span-green">{t("add-choices")}</span>
            </div>
            {answerType !== "pulldown" && (
              <div>
                <span className="span-or">{t("or")}</span>
                <span
                  className={
                    !handleCheckIsOtherExist(
                      question.questionnaire_answers,
                      "is_other"
                    ) ||
                    handleCheckIsOtherExist(
                      question.questionnaire_answers,
                      "_destroy"
                    )
                      ? "span-green"
                      : "disabled"
                  }
                  onClick={() => {
                    if (
                      !handleCheckIsOtherExist(
                        question.questionnaire_answers,
                        "is_other"
                      ) ||
                      handleCheckIsOtherExist(
                        question.questionnaire_answers,
                        "_destroy"
                      )
                    ) {
                      handleAddChoices(index, "other");
                    }
                  }}
                >
                  {t("added-other")}
                </span>
              </div>
            )}
          </WrapperAdd>
        )}
      </React.Fragment>
    );
  };

  const handleTitleRight = (currentQuestion: any) => {
    const currentLanguage = localStorageHelper.getItem("i18nextLng");
    let text = "";

    if (questionnaires.questionnaire_questions) {
      questionnaires.questionnaire_questions
        .filter((qs: any) => qs.is_answer_link_question)
        .map((item: any) => {
          if (item.questionnaire_answers.length) {
            item.questionnaire_answers.forEach((as: any) => {
              if (
                as?.link_questionnaire_question &&
                Object.keys(as.link_questionnaire_question).length &&
                as.link_questionnaire_question.question_position ===
                  currentQuestion.question_position
              ) {
                text =
                  currentLanguage === "ja"
                    ? `質問${item.question_position}「${as.answer_text}」から`
                    : `From question ${item.question_position} "${as.answer_text}"`;
              }
            });
          }
        });
    }

    return text;
  };

  return (
    <>
      <QuestionDraftTitle
        bg={question?.is_link_question ? "#EBC300" : "#2F8CAE"}
      >
        <QuestionDraftTitleLeft>
          {question?.is_link_question && <LinkedIcon />}{" "}
          <div style={{ width: "max-content" }}>
            {t("question") + `${index + 1}` + (question?.copy_idx || "")}
          </div>
        </QuestionDraftTitleLeft>

        {question?.is_link_question && (
          <QuestionDraftTitleRight>
            {handleTitleRight(question)}
          </QuestionDraftTitleRight>
        )}
      </QuestionDraftTitle>

      <QuestionDraftBody>
        <DragSection>
          <div></div>
          <div></div>
        </DragSection>
        <QuestionBodyLeft>
          <Input
            name="hello"
            value={question?.question_content}
            height={40}
            fsLabel={16}
            placeholder={t("please-select-type")}
            onChange={handleOnChange(index, "question_content")}
            marginForm="0px 0px 24px"
            error={error?.question_content || ""}
          />
          {question.is_free_text && (
            <TextArea
              value={question.question_summary}
              height="80px"
              fsLabel={16}
              marginForm="0px 0px 18px"
              onChange={handleOnChange(index, "question_summary")}
              error={error?.question_summary || ""}
            />
          )}
          <Select
            onChange={handleOnChangeQuestionType(index)}
            options={optionQuestions}
            height="40px"
            width={screens.xs ? "100%" : "214px"}
            marginForm="0px 0px 24px"
            placeholder={t1("please-select")}
            value={question.answer_type}
            error={error?.answer_type || ""}
          />

          {question.answer_type == "text" && (
            <>
              <Input
                value={
                  !question.questionnaire_answers[0]?._destroy
                    ? question.questionnaire_answers[0]?.answer_text
                    : ""
                }
                height={31}
                fsLabel={16}
                marginForm="0 0 8px"
                disabled
              />
              <WrapperTitleInputTextarea>
                {t("text-description-formula")}
              </WrapperTitleInputTextarea>
            </>
          )}

          {question.answer_type == "multiLine" && (
            <>
              <TextArea
                value={
                  !question.questionnaire_answers[0]?._destroy
                    ? question.questionnaire_answers[0]?.answer_text
                    : ""
                }
                height={"120px"}
                fsLabel={16}
                disabled
              />
              <WrapperTitleInputTextarea>
                {t("text-description-formula-textarea")}
              </WrapperTitleInputTextarea>
            </>
          )}

          {question.answer_type !== "text" &&
            question.answer_type !== "multiLine" &&
            handleRenderItem(question.answer_type)}
        </QuestionBodyLeft>

        <QuestionBodyRight>
          <CheckboxStyled
            checked={question.is_required}
            onChange={handleCheckbox(index, 1)}
          >
            <p className="p-title-question">{t("make-it-mandatory")}</p>
          </CheckboxStyled>

          {question.answer_type === "radio" && (
            <CheckboxStyled
              disabled={question.is_link_question}
              checked={question.is_answer_link_question}
              onChange={handleCheckbox(index, 2)}
            >
              <p className="p-title-question">{t("link-question")}</p>
            </CheckboxStyled>
          )}

          <CheckboxStyled
            checked={question.is_free_text}
            onChange={handleCheckbox(index, 3)}
          >
            <p className="p-title-question">{t("enter-a-description")}</p>
          </CheckboxStyled>

          <WrapperIconItemQuestion
            onClick={() => {
              handleCopyQuestion(question, index);
            }}
          >
            <CopyIcon fill="#3B3B3B" style={{ width: "24px" }} />
            <div>{t("copy-item")}</div>
          </WrapperIconItemQuestion>

          <WrapperIconItemQuestion
            style={screens.xs ? { marginLeft: 37 } : {}}
            onClick={() => {
              handleDeleteQuestion(question);
            }}
          >
            <TrashIcon
              fill="#FB4E4E"
              style={{
                marginRight: "8px",
              }}
            />
            <div style={{ color: "#FB4E4E" }}>{t2("delete")}</div>
          </WrapperIconItemQuestion>
        </QuestionBodyRight>
      </QuestionDraftBody>
    </>
  );
};

const QuestionDraft = ({
  questionnaires,
  setQuestionnaires,
  handleSaveDraft,
  isLoading,
  error,
  setError,
  status,
  setScreen,
  linkQuestionairesOptions,
  setLinkQuestionairesOptions,
  handleLinkQuestionOptions,
  defaultOptions,
}: {
  questionnaires: IQuestionnaire;
  setQuestionnaires: React.Dispatch<React.SetStateAction<IQuestionnaire>>;
  handleSaveDraft: (questions: any) => void;
  isLoading: boolean;
  status: string;
  error: any;
  setError: React.Dispatch<
    React.SetStateAction<{
      title: string;
      explanation: string;
      questions: string;
    }>
  >;
  setScreen: any;
  linkQuestionairesOptions: any;
  setLinkQuestionairesOptions: any;
  handleLinkQuestionOptions: any;
  defaultOptions: any;
}) => {
  const { t }: any = useTranslation("translation", { keyPrefix: "questions" });
  const dispatch = useDispatch();
  const deleteQuestionnairesData = useSelector(
    deleteQuestionnairesDataResponse
  );
  const [isVisible, setIsVisible] = useState<any>(false);
  const [isVisibleDeleteSurveyModal, setIsVisibleDeleteSurveyModal] =
    useState(false);

  const createQuestion = () => {
    let obj: any = {};

    obj = {
      ...questionnaires,
      questionnaire_questions: [
        ...(questionnaires?.questionnaire_questions || []),
        {
          id: uuid(),
          answer_type: "",
          question_content: "",
          question_summary: "",
          is_required: false,
          is_link_question: false,
          is_free_text: false,
          question_position: questionnaires.questionnaire_questions
            ? questionnaires.questionnaire_questions.length + 1
            : 1,
          questionnaire_answers: [],
        },
      ],
    };

    setLinkQuestionairesOptions([
      ...handleLinkQuestionOptions(obj.questionnaire_questions),
    ]);
    setQuestionnaires(obj);

    setError((prevState: any) => ({
      ...prevState,
      questions: "",
      questionnaire_questions: [
        ...prevState.questionnaire_questions,
        {
          id: uuid(),
          questionnaire_answers: [],
        },
      ],
    }));
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setQuestionnaires((prevState: any) => ({
      ...prevState,
      [name]: value,
    }));
    setError((prevState: any) => ({
      ...prevState,
      [name]: "",
    }));
  };

  const TooltipCustom = ({
    isVisible,
    onClose,
    setIsVisibleDeleteSurveyModal,
  }: any) => {
    const tooltipRef = useRef<any>(null);

    useEffect(() => {
      const handleClickOutside = (event: any) => {
        if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
          onClose();
        }
      };

      if (isVisible) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isVisible, onClose]);

    if (!isVisible) return null;

    return (
      <WrapperTooltip>
        <div
          ref={tooltipRef}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          onClick={() => setIsVisibleDeleteSurveyModal(true)}
        >
          <div style={{ display: "flex", cursor: "pointer" }}>
            {t("delete-survey")}
          </div>

          <DeleteIcon fill="#FB4E4E" height="17px" width="17px" />
        </div>
      </WrapperTooltip>
    );
  };

  useEffect(() => {
    if (
      deleteQuestionnairesData &&
      deleteQuestionnairesData.status === httpStatus.StatusOK
    ) {
      dispatch(DeleteQuestionnaireActionCreators.handleResetAction());
      dispatch(QuestionnairesActionCreators.getQuestionnairesAction());
      setScreen("");
      notification.success({
        placement: "bottomRight",
        message: t("delete-success"),
        icon: <SendCompleteIcon />,
      });
    }
  }, [deleteQuestionnairesData]);

  const moveCardHandler = (dragIndex: any, hoverIndex: any) => {
    if (questionnaires?.questionnaire_questions) {
      const dragItem = questionnaires?.questionnaire_questions[dragIndex];

      if (dragItem) {
        const coppiedArray = [...questionnaires?.questionnaire_questions];
        const prevItem = coppiedArray.splice(hoverIndex, 1, dragItem);
        coppiedArray.splice(dragIndex, 1, prevItem[0]);

        const couple = [hoverIndex + 1, dragIndex + 1];

        const newArr = coppiedArray.map((item: any) => {
          if (item.is_answer_link_question) {
            return {
              ...item,
              questionnaire_answers: item.questionnaire_answers.map(
                (as: any) => {
                  if (as.link_questionnaire_question_id === couple[0]) {
                    return {
                      ...as,
                      link_questionnaire_question: {
                        question_position: couple[1],
                      },
                      link_questionnaire_question_id: couple[1],
                      isChanged: true,
                    };
                  }
                  return as;
                }
              ),
            };
          }
          return item;
        });

        const newArr2 = newArr.map((item: any) => {
          if (item.is_answer_link_question) {
            return {
              ...item,
              questionnaire_answers: item.questionnaire_answers.map(
                (as: any) => {
                  if (
                    as.link_questionnaire_question_id === couple[1] &&
                    !as.isChanged
                  ) {
                    return {
                      ...as,
                      link_questionnaire_question_id: couple[0],
                      link_questionnaire_question: {
                        question_position: couple[0],
                      },
                    };
                  }
                  return as;
                }
              ),
            };
          }
          return item;
        });

        const modifiedQuestionPosition = newArr2.map((qs: any, idx) => {
          return {
            ...qs,
            question_position: idx + 1,
          };
        });

        setLinkQuestionairesOptions([
          // ...defaultOptions,
          ...handleLinkQuestionOptions(modifiedQuestionPosition),
        ]);

        if (questionnaires.id) {
          const coppiedObj = {
            ...questionnaires,
            questionnaire_questions: modifiedQuestionPosition,
          };
          handleSaveDraft(coppiedObj);
        } else {
          setQuestionnaires((prevState) => ({
            ...prevState,
            questionnaire_questions: modifiedQuestionPosition,
          }));
        }
      }
    }
  };

  return (
    <React.Fragment>
      <WrapperDraft>
        {status !== "create" && (
          <div
            style={{ position: "absolute", right: 11, top: -38, height: 24 }}
          >
            <div style={{ position: "relative", cursor: "pointer" }}>
              <MoreIcon
                // style={{ position: "absolute", right: 11, top: -38 }}
                fill="#B7B7B7"
                onClick={() => {
                  setIsVisible(true);
                }}
              />
              <TooltipCustom
                isVisible={isVisible}
                onClose={() => setIsVisible(false)}
                surveyID={questionnaires.id}
                setIsVisibleDeleteSurveyModal={setIsVisibleDeleteSurveyModal}
              />
            </div>
          </div>
        )}
        <Input
          name="title"
          value={questionnaires?.title || ""}
          label={
            <div style={{ fontWeight: 700 }}>{t("questionnaire-name")}</div>
          }
          height={31}
          fsLabel={16}
          marginForm="0px 0px 24px"
          onChange={handleOnChange}
          error={error?.title ? t(error?.title) : ""}
        />
        <TextAreaStyled
          name="explanation"
          value={questionnaires?.explanation || ""}
          label={
            <div style={{ fontWeight: 700 }}>
              {t("questionnaire-explanation")}
            </div>
          }
          placeholder={t("please-enter-a-description-of-the-survey")}
          marginForm="0px 0px 18px"
          fsLabel={16}
          lhLabel="24px"
          bdr="6px"
          onChange={handleOnChange}
          error={error?.explanation ? t(error?.explanation) : ""}
        />
        <div>
          <Button
            type="submit"
            name={t(status !== "create" ? "overwrite-save" : "save-survey")}
            background="#2AC769"
            color="#FFFFFF"
            border="none"
            fontSize={16}
            fontWeight={700}
            padding="7px 24px"
            disabled={isLoading}
            onClick={() => {
              handleSaveDraft(questionnaires);
            }}
          />
        </div>
      </WrapperDraft>

      {isVisibleDeleteSurveyModal && (
        <ConfirmModal
          isVisible={isVisibleDeleteSurveyModal}
          close={() => {
            setIsVisibleDeleteSurveyModal(false);
          }}
          ok={() => {
            setIsVisibleDeleteSurveyModal(false);
            if (questionnaires?.id) {
              dispatch(
                DeleteQuestionnaireActionCreators.deleteQuestionnairesAction({
                  id: questionnaires?.id,
                })
              );
            }
          }}
        >
          <TextConfirm>{t("is-delete-survey")}</TextConfirm>
        </ConfirmModal>
      )}

      <DndProvider backend={HTML5Backend}>
        {(questionnaires?.questionnaire_questions || []).map(
          (question, index) => {
            if (question._destroy) return;
            return (
              <MovableItem
                key={question.id}
                index={index}
                name={question.question_content}
                moveCardHandler={moveCardHandler}
              >
                <Question
                  key={index}
                  index={index}
                  question={question}
                  questionnaires={questionnaires}
                  setQuestionnaires={setQuestionnaires}
                  error={error.questionnaire_questions[index]}
                  setError={setError}
                  linkQuestionairesOptions={linkQuestionairesOptions}
                  setLinkQuestionairesOptions={setLinkQuestionairesOptions}
                  handleLinkQuestionOptions={handleLinkQuestionOptions}
                  defaultOptions={defaultOptions}
                  createQuestion={createQuestion}
                />
              </MovableItem>
            );
          }
        )}
      </DndProvider>

      <WrapperPlus>
        <div onClick={createQuestion} style={{ position: "relative" }}>
          <PlusIcon style={{ width: "28px", height: "28px" }} fill="#ffffff" />
          {error?.questions && (
            <WrapperErrorMessage>
              <ErrorMessage style={{ width: "max-content" }}>
                {t(error?.questions)}
              </ErrorMessage>
            </WrapperErrorMessage>
          )}
        </div>
      </WrapperPlus>
    </React.Fragment>
  );
};

export default QuestionDraft;
